import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectHasSingleBrandAndStory, selectHasSingleBrand } from '../features/channelSlice';
import { closeSidePanel } from '../features/sidePanelSlice';
import { toggleTheme, toggleDevice } from '../features/deviceSlice';
import { setSelectedLocale } from '../features/audioSlice';
import { CALL_LANGUAGES } from '../constants/callLanguages';
import { selectIsAuthenticated, signOut } from '../features/authSlice';
import { openLiveModal } from '../features/liveSlice';
import BrandSwitcher from './BrandSwitcher';
import AudioDevicePopover from './AudioDevicePopover';
import { startTour, resetTour } from './GuidedTour';
import { sendCaraQuit } from '../utils/caraBridge';
import { resetExperience, teardownExperience } from '../utils/resetExperience';

const ActionButton = ({ icon, title, onClick, active = false, mobile = false, disabled = false }) => (
  <div 
    className={`action-button ${mobile ? 'flex flex-col items-center' : 'relative group'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} 
    onClick={disabled ? undefined : onClick}
    data-action={title?.toLowerCase().replace(/\s+/g, '-')}
    title={disabled ? `${title} (unavailable)` : title}
  >
    <button
      disabled={disabled}
      style={{ 
        width: mobile ? 48 : 44, 
        height: mobile ? 48 : 44, 
        minWidth: mobile ? 48 : 44, 
        minHeight: mobile ? 48 : 44,
        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
      className={`
        relative overflow-hidden text-white p-2 rounded-xl transition-all duration-300 transform 
        flex items-center justify-center
        ${disabled
          ? 'bg-gradient-to-br from-[#1a1f2a] to-[#0f1419] opacity-40 cursor-not-allowed border border-gray-600/30'
          : active 
            ? 'bg-gradient-to-br from-[#44C2FD] to-[#23A6D5] hover:from-[#3AB0E8] hover:to-[#1E92BA] shadow-lg shadow-[#44C2FD]/40 scale-105 border border-white/20' 
            : 'bg-gradient-to-br from-[#2C3544] to-[#1a2332] hover:from-[#3a4556] hover:to-[#2C3544] backdrop-blur-sm border border-white/30 hover:border-white/50 shadow-md'
        }
        ${!disabled && 'hover:scale-110 hover:shadow-xl'}
        ${active ? 'ring-2 ring-[#44C2FD]/50' : ''}
      `}
      aria-label={title}
      title={disabled ? `${title} (No events to recap)` : title}
    >
      {/* Animated glow effect for active state */}
      {active && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50"></div>
          <div 
            className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent"
            style={{
              animation: 'shine 2s ease-in-out infinite',
            }}
          />
        </>
      )}
      
      {/* Icon */}
      <span className={`material-icons-outlined text-xl relative z-10 ${active ? 'drop-shadow-lg' : ''} ${disabled ? 'opacity-50' : ''}`}>
        {icon}
      </span>
      
      {/* Hover shimmer effect for inactive state */}
      {!active && !disabled && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
      )}
    </button>
    
    {/* Tooltip label — sits to the RIGHT of the button (the toolbar is on the
        left edge). Visible on hover, and always shown while the button is active. */}
    {!mobile && (
      <span
        className={`absolute left-full ml-3 top-1/2 transform -translate-y-1/2 text-white text-xs font-semibold tracking-wider uppercase transition-opacity duration-300 pointer-events-none whitespace-nowrap ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        style={{ fontSize: '8pt' }}
      >
        {title}
      </span>
    )}
    
    <style>{`
      @keyframes shine {
        0%, 100% { transform: translateX(-100%); }
        50% { transform: translateX(100%); }
      }
    `}</style>
  </div>
);

export default function Actions({ onShowSettings, onShowBrandSwitcher, mobile = false }) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const onboardingEvents = useSelector((state) => state.channel.onboardingEvents);
  const hasSingleBrandAndStory = useSelector(selectHasSingleBrandAndStory);
  const hasSingleBrand = useSelector(selectHasSingleBrand);
  const isHelpModalOpen = useSelector((state) => state.channel.isHelpModalOpen);
  const isLiveModalOpen = useSelector((state) => state.live.isLiveModalOpen);
  const eventHistory = useSelector((state) => state.live.eventHistory);
  const theme = useSelector((state) => state.device.theme);
  const deviceType = useSelector((state) => state.device.type);
  const selectedLocale = useSelector((state) => state.audio.selectedLocale);

  const [showBrandSwitcher, setShowBrandSwitcher] = React.useState(false);
  const [showDevices, setShowDevices] = React.useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = React.useState(false);
  const langMenuRef = React.useRef(null);

  // Close the language menu on outside click.
  React.useEffect(() => {
    if (!showLanguageMenu) return;
    const onDown = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setShowLanguageMenu(false);
      }
    };
    const t = setTimeout(() => document.addEventListener('pointerdown', onDown), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [showLanguageMenu]);
  
  // Close BrandSwitcher when Help or Live modal opens (desktop only)
  React.useEffect(() => {
    if (!mobile && showBrandSwitcher && (isHelpModalOpen || isLiveModalOpen)) {
      setShowBrandSwitcher(false);
    }
  }, [isHelpModalOpen, isLiveModalOpen, mobile, showBrandSwitcher]);

  // Close Devices popover when Help or Live modal opens (desktop only)
  React.useEffect(() => {
    if (!mobile && showDevices && (isHelpModalOpen || isLiveModalOpen)) {
      setShowDevices(false);
    }
  }, [isHelpModalOpen, isLiveModalOpen, mobile, showDevices]);
  
  // Check if authentication is enabled
  const authEnabled = process.env.REACT_APP_SKIP_AUTH !== 'true';
  
  const handleHelp = () => {
    dispatch(closeSidePanel()); // Close side panel first
    setShowBrandSwitcher(false); // Close BrandSwitcher if open
    dispatch({ type: 'channel/toggleHelpModal' });
  };

  const handleGuidedTour = () => {
    dispatch(closeSidePanel()); // Close side panel first
    setShowBrandSwitcher(false); // Close BrandSwitcher if open
    // Reset and start the tour
    resetTour();
    startTour();
  };
  
  const handleSettings = () => {
    dispatch(closeSidePanel()); // Close side panel first
    setShowBrandSwitcher(false); // Close BrandSwitcher if open
    if (onShowSettings) {
      onShowSettings();
    }
  };
  
  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all messages, emails, and notifications?")) {
      resetExperience({ dispatch, onboardingEvents });
    }
  };

  const handleSignOut = async () => {
    // Native iOS handles its own confirm UI — fire the bridge first, then tear
    // down so the WebView is clean if it gets reused.
    if (sendCaraQuit()) {
      teardownExperience({ dispatch });
      return;
    }

    if (!window.confirm("Are you sure you want to sign out?")) return;

    await teardownExperience({ dispatch });
    dispatch(signOut());
  };

  const handleLive = () => {
    dispatch(closeSidePanel()); // Close side panel first
    setShowBrandSwitcher(false); // Close BrandSwitcher if open
    dispatch(openLiveModal());
  };

  return (
    <div className={`actions-container ${mobile ? 'flex flex-row items-center space-x-2' : 'flex flex-col items-center h-full'}`}>
      {/* Cara logo at the top of the desktop toolbar. (Customer logo lives in
          the top-right corner, set in App.) */}
      {!mobile && (
        <div className="flex flex-col items-center mb-5 shrink-0">
          <img
            className="cara-toolbar-logo"
            src="/images/cara-white.png"
            alt="Cara"
            style={{ height: 44, maxWidth: 58, objectFit: 'contain', filter: 'brightness(1.15)' }}
          />
        </div>
      )}

      {/* Middle: channel / action buttons (display:contents on mobile keeps them
          in the parent's horizontal row). */}
      <div className={mobile ? 'contents' : 'flex flex-col items-center space-y-2'}>
      {/* Settings button - Opens consolidated settings popover (desktop only) */}
      {!mobile && (
        <div className="relative">
          <ActionButton
            icon="settings"
            title="Settings"
            onClick={() => {
              setShowDevices(!showDevices);
              setShowBrandSwitcher(false);
            }}
            active={showDevices}
            mobile={mobile}
          />
          <AudioDevicePopover
            isOpen={showDevices}
            onClose={() => setShowDevices(false)}
            anchor="right"
          />
        </div>
      )}
      {/* Story - show if user has multiple brands or configurations */}
      {!hasSingleBrandAndStory && (
        <ActionButton
          icon="auto_stories"
          title="Story"
          onClick={handleSettings}
          mobile={mobile}
        />
      )}
      {/* Brand Switcher - Hot-swap brands - Only show if user has multiple brands */}
      {!hasSingleBrand && (
        <div className="relative">
          <ActionButton
            icon="palette"
            title="Brand"
            onClick={() => {
              if (mobile) {
                // On mobile, trigger App-level modal
                onShowBrandSwitcher && onShowBrandSwitcher();
              } else {
                // On desktop, toggle local popout
                setShowBrandSwitcher(!showBrandSwitcher);
              }
            }}
            active={showBrandSwitcher}
            mobile={mobile}
          />
          {/* Desktop popout - render next to button */}
          {!mobile && (
            <BrandSwitcher
              isOpen={showBrandSwitcher}
              onClose={() => setShowBrandSwitcher(false)}
              mobile={false}
            />
          )}
        </div>
      )}
      {/* Default Agent Language menu - desktop only (in the popover on mobile) */}
      {!mobile && (
        <div className="relative" ref={langMenuRef}>
          <ActionButton
            icon="translate"
            title="Language"
            onClick={() => {
              setShowLanguageMenu((v) => !v);
              setShowDevices(false);
              setShowBrandSwitcher(false);
            }}
            active={showLanguageMenu}
            mobile={mobile}
          />
          {showLanguageMenu && (
            <div
              style={{
                position: 'absolute',
                left: 'calc(100% + 12px)',
                top: 0,
                width: 250,
                maxHeight: 360,
                overflowY: 'auto',
                background: 'var(--cara-surface)',
                border: '1px solid rgba(68, 194, 253, 0.25)',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                zIndex: 10001,
                padding: 6,
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--cara-text-muted)', padding: '6px 10px 8px' }}>
                Default Agent Language
              </div>
              {CALL_LANGUAGES.map((l) => {
                const selected = selectedLocale === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => {
                      dispatch(setSelectedLocale(l.code));
                      setShowLanguageMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: 'none',
                      background: selected ? 'rgba(68, 194, 253, 0.15)' : 'transparent',
                      color: selected ? '#44C2FD' : 'var(--cara-text)',
                      fontSize: 13,
                      fontWeight: selected ? 600 : 400,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'rgba(127,127,127,0.12)'; }}
                    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* Device frame (Phone / Tablet) toggle - desktop only (in the popover on mobile) */}
      {!mobile && (
        <ActionButton
          icon={deviceType === 'phone' ? 'smartphone' : 'tablet'}
          title={deviceType === 'phone' ? 'Device: Phone' : 'Device: Tablet'}
          onClick={() => dispatch(toggleDevice())}
          mobile={mobile}
        />
      )}
      {/* Light / dark theme switch for the native Cara chrome (desktop only) */}
      {!mobile && (
        <ActionButton
          icon={theme === 'light' ? 'dark_mode' : 'light_mode'}
          title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          onClick={() => dispatch(toggleTheme())}
          mobile={mobile}
        />
      )}
      </div>

      {/* Recap, Tour, Help, Reset and Quit pinned to the bottom of the toolbar (mt-auto). */}
      <div className={mobile ? 'contents' : 'mt-auto flex flex-col items-center space-y-2 pt-3 shrink-0'}>
        {/* Recap - only when live events are available */}
        {eventHistory && eventHistory.length > 0 && (
          <ActionButton
            icon="videocam"
            title="Recap"
            onClick={handleLive}
            mobile={mobile}
          />
        )}
        <ActionButton
          icon="route"
          title="Tour"
          onClick={handleGuidedTour}
          mobile={mobile}
        />
        <ActionButton
          icon="help_outline"
          title="Help"
          onClick={handleHelp}
          mobile={mobile}
        />
        <ActionButton
          icon="refresh"
          title="Reset"
          onClick={handleReset}
          mobile={mobile}
        />
        {authEnabled && isAuthenticated && (
          <ActionButton
            icon="logout"
            title="Quit"
            onClick={handleSignOut}
            mobile={mobile}
          />
        )}
      </div>
    </div>
  );
};
